import React, { useEffect } from 'react';
import './detail-drawer.css';

/**
 * Coque de la fiche en lecture : voile, panneau de 620 px à droite,
 * fermeture par Échap ou clic sur le voile
 */
export const DrawerShell = ({ ariaLabel, onClose, children }) => {
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <>
      <div className="agv-drawer-veil" onClick={onClose} />
      <aside className="agv-drawer" role="dialog" aria-label={ariaLabel}>
        {children}
      </aside>
    </>
  );
};

/**
 * Ligne de chiffres clés sous l'en-tête : [{ label, value, hint, className }]
 */
export const DrawerFacts = ({ facts }) => (
  <div className="agv-drawer__facts">
    {facts.map((fact) => (
      <div key={fact.label} className="agv-drawer-fact">
        <span className="agv-drawer-fact__label">{fact.label}</span>
        <span className={`agv-drawer-fact__value ${fact.className || ''}`}>
          {fact.value}
        </span>
        {fact.hint && (
          <span className="agv-drawer-fact__hint">{fact.hint}</span>
        )}
      </div>
    ))}
  </div>
);

/**
 * Section titrée en petites capitales, avec une action ou une méta à droite
 */
export const DrawerSection = ({ title, action, children }) => (
  <div className="agv-drawer__section">
    <div className="agv-drawer__section-head">
      <span className="agv-drawer__section-title">{title}</span>
      <span className="agv-drawer__spacer" />
      {action}
    </div>
    {children}
  </div>
);

/**
 * Libellé à gauche, valeur et indice à droite
 */
export const DrawerField = ({ label, value, hint, valueClassName }) => (
  <div className="agv-drawer-field">
    <span className="agv-drawer-field__label">{label}</span>
    <div className="agv-drawer-field__body">
      <span className={`agv-drawer-field__value ${valueClassName || ''}`}>
        {value}
      </span>
      {hint && <span className="agv-drawer-field__hint">{hint}</span>}
    </div>
  </div>
);

const itemsWeightFormatter = new Intl.NumberFormat('fr-FR', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/**
 * Tableau des articles d'un BL : référence, désignation, quantité, poids
 * (poids unitaire × quantité) et mètres linéaires
 */
export const DrawerItems = ({ items = [] }) => {
  if (items.length === 0) {
    return (
      <span className="agv-drawer-field__value--muted">Aucun article</span>
    );
  }

  return (
    <div className="agv-drawer-items">
      <div className="agv-drawer-items__row agv-drawer-items__row--head">
        <span className="agv-drawer-items__ref">Référence</span>
        <span className="agv-drawer-items__name">Désignation</span>
        <span className="agv-drawer-items__qty">Qté</span>
        <span className="agv-drawer-items__weight">Poids</span>
        <span className="agv-drawer-items__lm">ML</span>
      </div>
      {items.map((item, index) => (
        <div
          key={item.id || `${item.item_key}-${index}`}
          className="agv-drawer-items__row"
        >
          <span className="agv-drawer-items__ref">{item.item_key || '—'}</span>
          <span className="agv-drawer-items__name">{item.name || '—'}</span>
          <span className="agv-drawer-items__qty">{item.quantity ?? 1}</span>
          <span className="agv-drawer-items__weight">
            {itemsWeightFormatter.format(
              (parseFloat(item.weight) || 0) *
                (parseInt(item.quantity, 10) || 1)
            )}{' '}
            kg
          </span>
          <span className="agv-drawer-items__lm">
            {item.linear_meters != null ? `${item.linear_meters} m` : '—'}
          </span>
        </div>
      ))}
    </div>
  );
};
